/**
 * Member CSV / e-mail-list parsing for the org members bulk add (plan §4.3a).
 *
 * Pure, framework-free (mirrors organizationValidation.ts). The admin UI
 * parses, validates and dedupes client-side, submits ONE bulk call, and
 * renders the backend's per-e-mail outcomes — so this util only has to turn
 * "whatever the admin pastes or uploads" into a clean address list.
 *
 * Two shapes of input, because tenants supply both:
 *
 *  1. **A real member-list CSV** — the file a customer sends, with a header row
 *     and columns like `Vorname;Nachname;E-Mail;Mobil`. Only the e-mail column
 *     is read. Everything else is ignored rather than reported: a 50-member
 *     list would otherwise come back with 150 "invalid" entries — every first
 *     name, surname and phone number — while quietly working.
 *  2. **A pasted address list** — one per line, or separated by `;` / `,` /
 *     tabs. This is the fallback when no e-mail header is found.
 *
 * Quotes are stripped and duplicates removed case-insensitively (first
 * occurrence wins).
 *
 * NOTE: names and phone numbers are deliberately DISCARDED, not parsed. The
 * bulk endpoint takes `emails: string[]` and nothing else, so a name read here
 * would have nowhere to go — the member's name arrives when they register.
 * Carrying names through is a backend change, tracked in #2102.
 */

import { isValidBillingEmail } from './organizationValidation';

export interface ParsedMemberEmails {
    /** Deduped, lowercased, well-formed addresses — the bulk payload. */
    valid: string[];
    /** Non-empty tokens that are not a well-formed address (shown to the admin). */
    invalid: string[];
    /** How many well-formed duplicates were dropped. */
    duplicateCount: number;
}

/** Cell values that mark a CSV header row, not an address. */
const HEADER_TOKENS = new Set([
    'email', 'e-mail', 'mail', 'e-mail-adresse', 'email-adresse',
    'email address', 'e-mail address', 'emailaddress',
]);

const clean = (value: string): string =>
    value.trim().replace(/^["']+|["']+$/g, '').trim();

/**
 * Which column holds the e-mail, if the first line is a header row.
 *
 * Returns the delimiter alongside the index: the same file must be split the
 * same way on every line, and guessing per line would misalign a row whose
 * name field happens to contain a comma.
 */
const findEmailColumn = (
    firstLine: string,
): { delimiter: string, index: number } | null => {
    for (const delimiter of [';', ',', '\t']) {
        if (!firstLine.includes(delimiter)) continue;
        const cells = firstLine.split(delimiter).map(clean);
        const index = cells.findIndex((cell) => HEADER_TOKENS.has(cell.toLowerCase()));
        if (index !== -1) return { delimiter, index };
    }
    return null;
};

export function parseMemberEmails(raw: string): ParsedMemberEmails {
    const lines = raw.split(/\r?\n/).filter((line) => line.trim() !== '');
    const header = lines.length > 0 ? findEmailColumn(lines[0]) : null;

    // A real CSV: read one column and ignore the rest. Without this every
    // name and phone number in the file is reported back as an invalid
    // address, which buries the addresses that genuinely are malformed.
    const tokens = header
        ? lines
            .slice(1)
            .map((line) => clean(line.split(header.delimiter)[header.index] ?? ''))
            .filter((token) => token.length > 0)
        : raw
            .split(/[\r\n;,\t]+/)
            .map(clean)
            .filter((token) => token.length > 0);

    const valid: string[] = [];
    const invalid: string[] = [];
    const seen = new Set<string>();
    let duplicateCount = 0;

    tokens.forEach((token) => {
        if (HEADER_TOKENS.has(token.toLowerCase())) {
            return;
        }
        if (!isValidBillingEmail(token)) {
            invalid.push(token);
            return;
        }
        const normalized = token.toLowerCase();
        if (seen.has(normalized)) {
            duplicateCount += 1;
            return;
        }
        seen.add(normalized);
        valid.push(normalized);
    });

    return { valid, invalid, duplicateCount };
}
